import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Input } from './Input';

/** Fixed "paper", as the drawn pad: a signature is printed on customer documents. */
const PAPER = '#ffffff';
const INK = '#1f2937';
const WIDTH = 600;
const HEIGHT = 140;
const SCRIPT_FONTS =
	"'Brush Script MT', 'Segoe Script', 'Snell Roundhand', 'Apple Chancery', 'Lucida Handwriting', cursive";

export interface TypedSignatureProps {
	/** The name as typed. */
	name: string;
	/** Called with the typed name and the signature as a PNG data URL (null when empty). */
	onChange: (name: string, signature: string | null) => void;
	onBlur?: () => void;
	label?: string;
	disabled?: boolean;
	id?: string;
	'aria-invalid'?: boolean;
	'aria-describedby'?: string;
}

/**
 * PD-13768 — a signature typed instead of drawn: the signer types their name and it is
 * set in a script face on white paper, as a PNG the same shape as a drawn one, so the
 * form, the PDF and the signing record treat both alike (the record notes which it was).
 */
export function TypedSignature({
	name,
	onChange,
	onBlur,
	label,
	disabled = false,
	id,
	'aria-invalid': ariaInvalid,
	'aria-describedby': ariaDescribedBy,
}: TypedSignatureProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const [preview, setPreview] = useState<string | null>(null);

	useEffect(() => {
		const canvas = canvasRef.current;
		const context = canvas?.getContext('2d') ?? null;
		if (canvas == null || context == null) {
			return;
		}
		context.fillStyle = PAPER;
		context.fillRect(0, 0, WIDTH, HEIGHT);
		const text = name.trim();
		if (text === '') {
			setPreview(null);
			return;
		}
		context.fillStyle = INK;
		context.textBaseline = 'middle';
		// Shrink long names to fit the line.
		let size = 64;
		do {
			context.font = `${String(size)}px ${SCRIPT_FONTS}`;
			size -= 4;
		} while (context.measureText(text).width > WIDTH - 40 && size > 20);
		context.fillText(text, 20, HEIGHT / 2);
		setPreview(canvas.toDataURL('image/png'));
	}, [name]);

	return (
		<div className="space-y-2">
			<Input
				id={id}
				value={name}
				placeholder={t('Type your full name')}
				aria-label={label != null ? t(label) : t('Typed signature')}
				aria-invalid={ariaInvalid}
				aria-describedby={ariaDescribedBy}
				disabled={disabled}
				autoComplete="name"
				onChange={(event) => {
					const next = event.target.value;
					// The image follows on the next paint; the answer updates with it below.
					onChange(next, null);
				}}
				onBlur={onBlur}
			/>
			<canvas ref={canvasRef} width={WIDTH} height={HEIGHT} className="hidden" />
			<SignaturePreviewSync name={name} preview={preview} onChange={onChange} />
			<div
				className={cn(
					'flex h-[100px] items-center rounded-md border px-3',
					colors.classes.borderDefault,
				)}
				style={{ backgroundColor: PAPER }}
				aria-hidden="true"
			>
				{preview != null ? (
					<img src={preview} alt="" className="max-h-[90px]" />
				) : (
					<span className="text-sm" style={{ color: '#6b7280' }}>
						{t('Your typed signature appears here')}
					</span>
				)}
			</div>
		</div>
	);
}

/** Reports the drawn image once it exists for the typed name. */
function SignaturePreviewSync({
	name,
	preview,
	onChange,
}: {
	name: string;
	preview: string | null;
	onChange: (name: string, signature: string | null) => void;
}): null {
	const last = useRef<string | null>(null);
	useEffect(() => {
		if (preview !== last.current) {
			last.current = preview;
			onChange(name, preview);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only a new image is news
	}, [preview]);
	return null;
}
